import React from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  BORDER, ERROR, SANS_STACK, SUCCESS, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';
import { SectionLabel } from '../common/hero/styles';
import { followableUrl } from '../common/hero/utils';
import useDragReorder from '../common/useDragReorder';
import PlatformIcon from './PlatformIcon';
import { profileMeta } from './utils';

const DND_TYPE = 'authorHeroProfile';

const focusRing = {
  borderColor: SUCCESS,
  boxShadow: '0 0 0 4px rgba(76,175,80,0.12)',
};

const fieldInputSx = {
  boxSizing: 'border-box',
  width: '100%',
  border: `1.5px solid ${BORDER}`,
  borderRadius: '8px',
  padding: '8px 11px',
  fontSize: 13,
  fontFamily: SANS_STACK,
  color: TEXT_DARK,
  background: '#fff',
  outline: 'none',
  '&:focus': focusRing,
};

// The URL field is a bordered row (platform logo + input + open-in-new-tab)
// styled to read like the plain inputs around it.
const urlRowSx = {
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  border: `1.5px solid ${BORDER}`,
  borderRadius: '8px',
  padding: '0 11px',
  background: '#fff',
  '&:focus-within': focusRing,
};

const bareInputSx = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  padding: '8px 0',
  fontSize: 13,
  fontFamily: SANS_STACK,
  color: TEXT_DARK,
  background: 'transparent',
};

function ProfileField({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{
        fontSize: 11, fontWeight: 600, color: TEXT_SECONDARY, mb: '5px',
      }}>
        {label}
      </Box>
      {children}
    </Box>
  );
}

ProfileField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function ProfileCard({
  profile, index, canDelete, setProfile, removeProfile, moveProfile,
}) {
  const coral = useTheme().palette.primary.main;
  const { rowRef: cardRef, handleRef, isDragging } = useDragReorder(DND_TYPE, index, moveProfile);

  const meta = profileMeta(profile);

  return (
    <Box
      ref={cardRef}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: '12px',
        border: '1px solid #e6e6e6',
        borderRadius: '10px',
        padding: '14px 16px 14px 12px',
        background: '#fafafa',
        opacity: isDragging ? 0.45 : 1,
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          alignSelf: 'center',
          display: 'flex',
          cursor: 'grab',
          color: '#c4c4c4',
          touchAction: 'none',
          '&:hover': { color: '#8a8a8a' },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 20 }} />
      </Box>

      {profile.imageUrl && (
        <Avatar
          src={profile.imageUrl}
          alt=""
          sx={{
            width: 54, height: 54, alignSelf: 'center', border: '1px solid rgba(0,0,0,.08)',
          }}
        />
      )}

      <Box sx={{
        flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px 12px',
      }}>
        <ProfileField label="Name">
          <Box
            component="input"
            value={profile.name || ''}
            placeholder="Display name"
            onChange={(e) => setProfile(index, 'name', e.target.value)}
            sx={fieldInputSx}
          />
        </ProfileField>
        <ProfileField label="Username">
          <Box
            component="input"
            value={profile.username || ''}
            placeholder="handle (no @)"
            onChange={(e) => setProfile(index, 'username', e.target.value)}
            sx={fieldInputSx}
          />
        </ProfileField>
        <ProfileField label="URL">
          <Box sx={urlRowSx}>
            {meta.url && <PlatformIcon platform={meta.platform} size={16} />}
            <Box
              component="input"
              value={profile.url || ''}
              placeholder="https://… (auto-detect)"
              onChange={(e) => setProfile(index, 'url', e.target.value)}
              sx={bareInputSx}
            />
            {followableUrl(meta.url) && (
              <Box
                component="a"
                href={followableUrl(meta.url)}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  color: '#8b8f96',
                  '&:hover': { color: coral },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 15 }} />
              </Box>
            )}
          </Box>
        </ProfileField>
        <ProfileField label="Image">
          <Box
            component="input"
            value={profile.imageUrl || ''}
            placeholder="Portrait image URL"
            onChange={(e) => setProfile(index, 'imageUrl', e.target.value)}
            sx={fieldInputSx}
          />
        </ProfileField>
      </Box>

      {canDelete && (
        <Box
          component="button"
          type="button"
          title="Delete profile"
          onClick={() => removeProfile(index)}
          sx={{
            alignSelf: 'center',
            border: 'none',
            background: 'none',
            p: '2px',
            cursor: 'pointer',
            color: '#c4c4c4',
            fontSize: 20,
            lineHeight: 1,
            '&:hover': { color: ERROR },
          }}
        >
          ×
        </Box>
      )}
    </Box>
  );
}

ProfileCard.propTypes = {
  profile: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  canDelete: PropTypes.bool.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeProfile: PropTypes.func.isRequired,
  moveProfile: PropTypes.func.isRequired,
};

// Edit-mode profiles editor: the identity header is derived from these
// records, so this is where the author is actually edited.
export default function ProfilesEditor({
  profiles, setProfile, addProfile, removeProfile, moveProfile,
}) {
  const { main: coral, tint: coralTint } = useTheme().palette.primary;
  return (
    <Box sx={{ mt: '24px' }}>
      <SectionLabel sx={{ mb: '10px' }}>Profiles</SectionLabel>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {profiles.map((profile, index) => (
          <ProfileCard
            // Profiles carry no stable id, so index keys are the best available.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            profile={profile}
            index={index}
            canDelete={profiles.length > 1}
            setProfile={setProfile}
            removeProfile={removeProfile}
            moveProfile={moveProfile}
          />
        ))}
      </Box>
      <Box
        component="button"
        type="button"
        onClick={addProfile}
        sx={{
          mt: '14px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          border: `1px dashed ${coral}`,
          borderRadius: '8px',
          padding: '8px 14px',
          fontSize: '.84rem',
          fontWeight: 700,
          color: coral,
          background: '#fff',
          cursor: 'pointer',
          fontFamily: SANS_STACK,
          '&:hover': { background: coralTint },
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        Add profile
      </Box>
    </Box>
  );
}

ProfilesEditor.propTypes = {
  profiles: PropTypes.array.isRequired,
  setProfile: PropTypes.func.isRequired,
  addProfile: PropTypes.func.isRequired,
  removeProfile: PropTypes.func.isRequired,
  moveProfile: PropTypes.func.isRequired,
};
